/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 12:14:08 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { isEmpty } from "lodash";
import { schemaBuilder } from "lib/validation";
import { Transformers } from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";

export interface NumberingSequence extends ProcessTemplateArtifact, WritableModel<false> {
  active: boolean;
  name?: string; // Auto assign if not provided
  code?: string; // Auto assign uuid if not provided
  prefix?: string;
  suffix?: string;
  padding?: number;
  number_next?: number;
  number_increment?: number;
  alphanumeric_next?: string;
}

/**
 * Transformers are run before validations and only applied for non strict schemas
 * https://github.com/jquense/yup#schematransformcurrentvalue-any-originalvalue-any--any-schema
 */

export const NumberingSequenceValidationSchema: ProcessTemplateArtifactSchemaType<NumberingSequence> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.number().positive().nullable(),
    active: schemaBuilder.boolean(),
    name: schemaBuilder.string().optional().nullable(),
    code: schemaBuilder.string().optional().nullable(),
    prefix: schemaBuilder.string().optional().nullable(),
    suffix: schemaBuilder.string().optional().nullable(),
    padding: schemaBuilder
      .number()
      .optional()
      .nullable()
      .when("alphanumeric_next", {
        is: (alphanumeric_next: string) => !isEmpty(alphanumeric_next),
        then: (schema) => schema.transform(Transformers.emptyNumberToZero),
        otherwise: (schema) =>
          schema
            .required("Padding is required when sequence is not an alphanumeric")
            .typeError("Padding must be a number. To use an alphanumeric sequence, Enter the next alphanumeric value"),
      }),
    number_next: schemaBuilder
      .number()
      .optional()
      .nullable()
      .when("alphanumeric_next", {
        is: (alphanumeric_next: string) => !isEmpty(alphanumeric_next),
        then: (schema) => schema.transform(Transformers.emptyNumberToZero),
        otherwise: (schema) =>
          schema
            .required("Number next is required when sequence is not an alphanumeric")
            .typeError(
              "Number next must be a number. To use an alphanumeric sequence, Enter the next alphanumeric value"
            ),
      }),
    number_increment: schemaBuilder
      .number()
      .optional()
      .nullable()
      .when("alphanumeric_next", {
        is: (alphanumeric_next: string) => !isEmpty(alphanumeric_next),
        then: (schema) => schema.transform(Transformers.emptyNumberToZero),
        otherwise: (schema) =>
          schema
            .required("Number increment is required when sequence is not an alphanumeric")
            .typeError(
              "Number increment must be a number. To use an alphanumeric sequence, Enter the next alphanumeric value"
            ),
      }),
    alphanumeric_next: schemaBuilder.string().optional().nullable(),
  });
